import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchAdminAccounts, getAccountBillingInfo, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'
import { QUERY_KEYS } from './queryKeys'

export const useSearchAccountsMultiple = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.searchAccountsMultiple, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'searchAccountsMultiple', query)
      return data
    },
    select: mapper,
    enabled
  })
}

export const useInvalidateSearchAccountsMultiple = () => {
  const client = useQueryClient()
  const { userId } = useAppContext()
  return useCallback(() => {
    console.debug('invalidating', QUERY_KEYS.searchAccountsMultiple)
    client.invalidateQueries({
      queryKey: [QUERY_KEYS.searchAccountsMultiple, userId],
      refetchType: 'all'
    }).catch(console.error)
  }, [client, userId])
}

export const useSearchAccounts = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper = null } = options
  return useQuery({
    queryKey: [QUERY_KEYS.searchAccounts, userId, query],
    queryFn: async ({ signal }) => {
      const { data } = await postNamedQuery(
        'levels',
        'searchAccounts',
        query,
        signal
      )
      return mapper ? mapper(data) : data
    },
    enabled
  })
}

export const useAccountBillingInfo = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper = null } = options
  return useQuery({
    queryKey: [QUERY_KEYS.accountBillingInfo, userId, query],
    queryFn: async () => {
      const { data } = await getAccountBillingInfo(query)
      return mapper ? mapper(data) : data
    },
    enabled
  })
}

export const useAccounts = (query, mapper = null) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.accounts, userId, query],
    queryFn: async () => {
      const { data } = await fetchAdminAccounts(query)
      return mapper ? mapper(data) : data
    }
  })
}

export const useListAssignedClientAccounts = (clientId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.listAssignedClientAccounts, userId, clientId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-client-accounts', {
        clientId
      })

      return data
    }
  })
}

export const useInvalidateListAssignedClientAccounts = (clientId) => {
  const client = useQueryClient()
  const { userId } = useAppContext()
  return useCallback(() => {
    console.debug('invalidating', QUERY_KEYS.listAssignedClientAccounts)
    client.invalidateQueries({
      queryKey: [QUERY_KEYS.listAssignedClientAccounts, userId, clientId],
      exact: true
    }).catch(console.error)
  }, [client, clientId, userId])
}
